import { useMemo, useState } from 'react'
import { clsx } from 'clsx'
import { resolveMediaUrl } from '../../utils/mediaUrl'
import { formatSetCode } from '../../utils/setCode'
import styles from './SetImage.module.css'

export interface SetImageProps {
  logoUrl?: string | null
  symbolUrl?: string | null
  /** A picture from the set's own cards, for sets no publisher logo exists for. */
  coverImageUrl?: string | null
  setCode?: string | null
  setName?: string | null
  /** Leave empty where the set's name is already next to the image. */
  alt?: string
  /** `sm` for result-row icons of about 32px, where the fallback text must shrink to fit. */
  size?: 'sm' | 'md'
  className?: string
}

/** Up to three initials, for the rare set with neither a picture nor a code. */
function initialsOf(setName: string | null | undefined): string {
  return String(setName ?? '')
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
    .map((word) => word[0]!.toUpperCase())
    .join('')
    .slice(0, 3)
}

/**
 * The one way to show a set's picture: logo, then symbol, then a cover from its own cards, moving
 * to the next whenever one fails to load, and the set code in a badge if they all do. Fills its
 * parent, which sets the size.
 */
export function SetImage({ logoUrl, symbolUrl, coverImageUrl, setCode, setName, alt = '', size = 'md', className }: SetImageProps) {
  const candidates = useMemo(() => {
    const urls = [logoUrl, symbolUrl, coverImageUrl].map((url) => resolveMediaUrl(url)).filter(Boolean) as string[]
    return [...new Set(urls)]
  }, [logoUrl, symbolUrl, coverImageUrl])

  const candidatesKey = candidates.join('|')
  const [failed, setFailed] = useState<{ key: string; count: number }>({ key: candidatesKey, count: 0 })
  // A new set in the same slot starts again from its first picture.
  const failedCount = failed.key === candidatesKey ? failed.count : 0
  const src = candidates[failedCount]

  if (src) {
    return (
      <img
        key={src}
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        className={clsx(styles.image, className)}
        onError={() => setFailed({ key: candidatesKey, count: failedCount + 1 })}
      />
    )
  }

  return (
    <span className={clsx(styles.badge, size === 'sm' && styles.small, className)} aria-hidden={alt ? undefined : true} aria-label={alt || undefined}>
      {formatSetCode(setCode) || initialsOf(setName) || 'SET'}
    </span>
  )
}

export default SetImage
